import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { Alert, Badge, Button, Card, Field, PageHeader, Select, Skeleton, Table } from "@/components/ui";

interface DispatchJob {
  id: string;
  shipment_id: string;
  driver_id?: string;
  driver_name?: string;
  type: "collection" | "delivery";
  status: string;
  waybill: string;
  service_level: string;
  address: { street?: string; suburb?: string; city?: string };
  created_at: string;
}

interface Driver {
  id: string;
  user_id: string;
  name: string;
  phone: string;
  vehicle_registration: string;
  is_on_shift: boolean;
  open_jobs: number;
}

interface Candidate {
  user_id: string;
  email: string;
  name: string;
}

/**
 * The dispatch board.
 *
 * Booking writes a collection and a delivery with no driver on them, and the
 * driver app asks for the stops assigned to it. Everything between those two
 * facts happens on this screen: without it a shipment is paid for, sitting in
 * the database, and on nobody's list.
 *
 * Unassigned work shows first and by default, because that is the question a
 * dispatcher opens the day with -- not "what is everyone doing" but "what is
 * nobody doing".
 */
export function Dispatch() {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState("");

  const jobs = useQuery({
    queryKey: ["dispatch-jobs", showAll],
    queryFn: () => api.get<{ data: DispatchJob[] }>(`/v1/ops/jobs${showAll ? "" : "?unassigned=true"}`),
    // A dispatcher leaves this open all day; without a refetch they are looking
    // at whatever was booked when they opened it.
    refetchInterval: 30_000,
  });
  const drivers = useQuery({
    queryKey: ["dispatch-drivers"],
    queryFn: () => api.get<{ data: Driver[] }>("/v1/ops/drivers"),
  });

  const assign = useMutation({
    mutationFn: ({ jobId, driverId }: { jobId: string; driverId: string }) =>
      api.post(`/v1/ops/jobs/${jobId}/assign`, { driver_id: driverId }),
    onMutate: () => setError(""),
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not assign that stop."),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["dispatch-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["dispatch-drivers"] });
    },
  });

  const onShift = (drivers.data?.data ?? []).filter((d) => d.is_on_shift);

  return (
    <>
      <PageHeader
        title="Dispatch"
        subtitle="Collections and deliveries waiting for a driver."
        action={
          <Button variant="secondary" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show unassigned only" : "Show everything open"}
          </Button>
        }
      />

      {error && <Alert>{error}</Alert>}

      {drivers.data && onShift.length === 0 && (
        <Alert tone="warn">
          Nobody is on shift. Stops can still be assigned, but no one is marked as driving today.
        </Alert>
      )}

      <Card className="mb-5">
        {jobs.isLoading || !jobs.data ? (
          <Skeleton className="h-48 w-full" />
        ) : jobs.data.data.length === 0 ? (
          <p className="text-sm text-ink-500">
            {showAll
              ? "Nothing open. Every stop is done or cancelled."
              : "Nothing waiting — every open stop has a driver."}
          </p>
        ) : (
          <Table head={["Waybill", "Stop", "Where", "Status", "Driver"]}>
            {jobs.data.data.map((job) => (
              <tr key={job.id}>
                <td className="ws-td font-mono text-xs">{job.waybill}</td>
                <td className="ws-td">
                  <Badge tone={job.type === "collection" ? "neutral" : "good"}>{job.type}</Badge>
                </td>
                <td className="ws-td text-ink-600">
                  {[job.address.street, job.address.suburb, job.address.city].filter(Boolean).join(", ")}
                </td>
                <td className="ws-td text-ink-500">{job.status}</td>
                <td className="ws-td">
                  <Select
                    label=""
                    aria-label={`Driver for ${job.waybill} ${job.type}`}
                    value={job.driver_id ?? ""}
                    disabled={assign.isPending}
                    onChange={(e) => assign.mutate({ jobId: job.id, driverId: e.target.value })}
                    options={[
                      { value: "", label: "— unassigned —" },
                      ...(drivers.data?.data ?? []).map((d) => ({
                        value: d.id,
                        // The open count is what a dispatcher actually chooses
                        // on: who is free.
                        label: `${d.name} (${d.open_jobs})${d.is_on_shift ? "" : " — off shift"}`,
                      })),
                    ]}
                  />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <DriverRoster drivers={drivers.data?.data ?? []} loading={drivers.isLoading} />
    </>
  );
}

/**
 * The fleet, and how to add to it.
 *
 * A driver is created from a login that already holds the driver role rather
 * than from an email and a password: minting credentials here would be a second
 * way to create users with its own ideas about passwords, and a driver record
 * against a login that cannot open the driver app does nothing at all.
 */
function DriverRoster({ drivers, loading }: { drivers: Driver[]; loading: boolean }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ user_id: "", name: "", phone: "", vehicle_registration: "" });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState("");

  const candidates = useQuery({
    queryKey: ["driver-candidates"],
    queryFn: () => api.get<{ data: Candidate[] }>("/v1/ops/drivers/candidates"),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["dispatch-drivers"] });
    queryClient.invalidateQueries({ queryKey: ["driver-candidates"] });
  };

  const create = useMutation({
    mutationFn: () => api.post("/v1/ops/drivers", form),
    onMutate: () => {
      setError("");
      setFields({});
    },
    onSuccess: () => {
      setForm({ user_id: "", name: "", phone: "", vehicle_registration: "" });
      refresh();
    },
    onError: (err) => {
      if (err instanceof ApiError && Object.keys(err.fields).length) setFields(err.fields);
      else setError(err instanceof ApiError ? err.message : "Could not add that driver.");
    },
  });

  const shift = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      api.patch(`/v1/ops/drivers/${id}/shift`, { is_on_shift: on }),
    onSettled: refresh,
  });

  return (
    <Card>
      <h2 className="mb-4 text-lg font-bold tracking-tight">Drivers</h2>

      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : drivers.length === 0 ? (
        <p className="mb-5 text-sm text-ink-500">
          No drivers yet. Until one exists, nothing booked can be collected.
        </p>
      ) : (
        <div className="mb-5">
          <Table head={["Name", "Phone", "Vehicle", "Open stops", "On shift"]}>
            {drivers.map((d) => (
              <tr key={d.id}>
                <td className="ws-td font-medium">{d.name}</td>
                <td className="ws-td text-ink-600">{d.phone}</td>
                <td className="ws-td text-ink-600">{d.vehicle_registration || "—"}</td>
                <td className="ws-td">{d.open_jobs}</td>
                <td className="ws-td">
                  <input
                    type="checkbox"
                    checked={d.is_on_shift}
                    onChange={() => shift.mutate({ id: d.id, on: !d.is_on_shift })}
                    className="h-4 w-4 rounded border-ink-300 text-brand"
                    aria-label={`${d.name} on shift`}
                  />
                </td>
              </tr>
            ))}
          </Table>
          <p className="mt-2 text-xs text-ink-400">
            Clocking off does not hand back the stops a driver is already carrying — reassign those above.
          </p>
        </div>
      )}

      <form
        className="grid gap-3 border-t border-ink-200 pt-5 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Select
          label="Driver login"
          className="sm:col-span-2"
          value={form.user_id}
          onChange={(e) => setForm({ ...form, user_id: e.target.value })}
          error={fields.user_id}
          options={[
            { value: "", label: candidates.data?.data.length ? "Choose a login" : "No unused driver logins" },
            ...(candidates.data?.data ?? []).map((c) => ({
              value: c.user_id,
              label: `${c.name || c.email} — ${c.email}`,
            })),
          ]}
        />
        <Field label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={fields.name} />
        <Field label="Phone" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} error={fields.phone} />
        <Field
          label="Vehicle registration"
          className="sm:col-span-2"
          value={form.vehicle_registration}
          onChange={(e) => setForm({ ...form, vehicle_registration: e.target.value })}
        />
        {error && <Alert>{error}</Alert>}
        <div className="sm:col-span-2">
          <Button type="submit" disabled={create.isPending || !form.user_id}>
            {create.isPending ? "Adding…" : "Add driver"}
          </Button>
          <p className="ws-hint mt-2">
            Only logins with the driver role appear here. Create one under Platform → Users first, then add them.
          </p>
        </div>
      </form>
    </Card>
  );
}
